import React, { FC, useEffect, useState } from 'react'
import { Menu, Dropdown, Tabs, Button, Row, Col, message, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { initialize, reset } from 'redux-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

// reducers
import { RootState } from '../../reducers'
import { logOutUser } from '../../reducers/authorization/authActions'
import { RESET_TEMPLATE_STORE, TEMPLATE_EDIT_CHANGE } from '../../reducers/templates/templateTypes'

// hooks
import useBackUrl from '../../hooks/useBackUrl'

// utils
import { postReq } from '../../utils/request'
import { ANT_MESSAGE_KEY, EDITING_TIME_MINUTES_LIMIT, FORM, PAGE, ROLES, TEMPLATE_BUILDER_HEADER_TABS } from '../../utils/enums'

// assets
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/BaseMiniIcons/chevron-down-3-icon.svg'
import { ReactComponent as LogOutIcon } from '../../assets/icons/BaseIcons/logout-1-icon.svg'
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/BaseIcons/arrow-left-1-icon.svg'
import { ReactComponent as AdminAvatar } from '../../assets/images/paradigma-admin-logo.svg'
import { ReactComponent as ExclamationIcon } from '../../assets/icons/BaseIcons/exclamation-circle-1-icon.svg'

// components
import FormSettingsModal from '../modals/FormSettingsModal'

// types
import { IForm } from '../../types/interfaces'

const { TabPane } = Tabs

type Props = {
	page?: PAGE
}

const TemplateBuilderHeader: FC<Props> = (props) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const data = useSelector((state: RootState) => state.template)
	const id = data.template.data?.id
	const loginUserData = useSelector((state: RootState) => state.auth)
	const [activeKey, setActiveKey] = useState<string>(props.page || '')
	const [backUrlLink] = useBackUrl()
	const disable = useSelector((state: RootState) => state.template.disable)
	const backUrlQuery = window.location.search
	const [formModalShowed, setFormModalShowed] = useState(false)
	const [processing, setProcessing] = useState(false)
	const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />
	const load = useSelector((state: RootState) => state.formTemplate.load)
	const BEVersion = useSelector((state: RootState) => state.versions.versions)
	const iFrameVersion = useSelector((state: RootState) => state.versions.iframeVersions)

	useEffect(() => {
		// check if from is edited
		const time = Math.abs(dayjs(data.template.data?.updatedAt || 0).diff(dayjs(), 'minutes'))
		// check editor id or latest updated time
		if (data.template.data?.editor?.id === loginUserData.authUser.data?.id || time >= EDITING_TIME_MINUTES_LIMIT || data.template.data?.editor === null) {
			const disabled = false
			dispatch({ type: TEMPLATE_EDIT_CHANGE, disabled })
			message.destroy(ANT_MESSAGE_KEY)
		} else if (data.template.data?.editor !== null) {
			// if admin editing form show message to another admins
			const disabled = true
			dispatch({ type: TEMPLATE_EDIT_CHANGE, disabled })
			message.destroy(ANT_MESSAGE_KEY)
			message.config({
				duration: 10000,
				maxCount: 1
			})
			message.info({
				icon: <ExclamationIcon width={20} height={20} />,
				content: t(`loc:This form is currently edited by ${data.template.data?.editor?.fullName}.`),
				key: ANT_MESSAGE_KEY
			})
		}
	}, [data.template, dispatch, t, loginUserData.authUser])

	// create form from template
	const createFormFromTemplate = async (formData: IForm) => {
		try {
			setProcessing(true)
			const newFrom = {
				formName: formData.name,
				formDatabase: formData.database,
				formQueue: formData.queue,
				formType: formData.type
			}
			const res = await postReq(`/api/admin/templates/${id}/apply`, undefined, newFrom)
			setFormModalShowed(false)
			const newTemplateId = res.data?.data?.formID
			navigate(`${t('paths:settings/{{templateId}}', { templateId: newTemplateId }) as string}?backUrl=${btoa(t('paths:forms'))}`)
		} catch (err: any) {
			setFormModalShowed(false)
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		} finally {
			setProcessing(false)
		}
	}

	const menu = (
		<Menu>
			<Menu.Item
				onClick={() => {
					dispatch(logOutUser())
				}}
				key='1'
			>
				<LogOutIcon /> {t('loc:Log out')}
			</Menu.Item>
			<div className={'version mx-3 pt-3'}>
				<div className={'version-info text-gray-500 text-xs tracking-wider'}>{`BE v${BEVersion.originalData}`}</div>
				<div className={'version-divider rounded-full mx-2'} />
				<div className={'version-info text-gray-500 text-xs tracking-wider'}>{`FE v${process.env.REACT_APP_VERSION}`}</div>
				{iFrameVersion?.originalData && (
					<>
						<div className={'version-divider rounded-full mx-2'} />
						<div className={'version-info text-gray-500 text-xs tracking-wider'}>{`iF v${iFrameVersion.originalData}`}</div>
					</>
				)}
			</div>
		</Menu>
	)

	/* routing to another components inside of form builder */
	const onTabClicked = (key: string) => {
		setActiveKey(key)
		if (key === TEMPLATE_BUILDER_HEADER_TABS.BUILDER) {
			navigate(`${t(`paths:/template-builder/${id}`)}${backUrlQuery}`)
		} else if (key === TEMPLATE_BUILDER_HEADER_TABS.PREVIEW) {
			navigate(`${t(`paths:/template-preview/${id}`)}${backUrlQuery}`)
		} else if (key === TEMPLATE_BUILDER_HEADER_TABS.SETTINGS) {
			navigate(`${t(`paths:/template-settings/${id}`)}${backUrlQuery}`)
		}
	}

	const backToList = () => {
		// reset form store
		dispatch({ type: RESET_TEMPLATE_STORE })
		message.destroy(ANT_MESSAGE_KEY)
		if (backUrlLink) {
			navigate(backUrlLink)
		}
	}

	const openAndInitFormModal = async () => {
		await setFormModalShowed(true)
		const templateData = {
			queue: data?.template.data?.formQueue?.title,
			database: data?.template.data?.formDatabase,
			type: data?.template.data?.formType
		}
		dispatch(initialize(FORM.GENERALE_TEMPLATE_ATTRIBUTES, templateData))
	}

	const renderUserRole = (role?: string) => {
		if (role === ROLES.ADMINISTRATOR) {
			return t('loc:Admin')
		}
		return role
	}

	return (
		<>
			<div className={'form-builder-header'}>
				<div className={'left-box-form-builder-header'}>
					<Dropdown className={'header-user-dropdown-box'} overlay={menu}>
						<div>
							<Row className={'w-full h-full'} justify={'space-between'}>
								<Col span={5}>
									<div className={'w-full h-full grid items-center'}>
										<AdminAvatar className={'m-0 ml-2 w-40px h-40px'} />
									</div>
								</Col>
								<Col span={16} className={'my-2'}>
									<div className={'grid grid-cols-1 gap-2 place-content-stretch user'}>
										<div className={'text-gray-900 font-medium leading-none'}>{loginUserData?.authUser?.data?.fullName}</div>
										<div className={'text-gray-500 font-regular leading-none user-role'}>
											{renderUserRole(loginUserData?.authUser?.data?.role)}
										</div>
									</div>
								</Col>
								<Col span={3}>
									<div className={'grid justify-items-center justify-start'}>
										<ChevronDownIcon className={'ml-auto'} />
									</div>
								</Col>
							</Row>
						</div>
					</Dropdown>
				</div>
				<div className={'middle-box-form-builder-header'}>
					<ul>
						<li className={'middle-box-form-builder-header-label'}>
							<ArrowLeftIcon onClick={backToList} /> <label>{data.template.data?.name}</label>
						</li>
						<li className={'middle-box-form-builder-header-nav'}>
							<Tabs onTabClick={onTabClicked} defaultActiveKey={activeKey}>
								<TabPane tab={t('loc:Builder')} key={TEMPLATE_BUILDER_HEADER_TABS.BUILDER} />
								<TabPane tab={t('loc:Preview')} key={TEMPLATE_BUILDER_HEADER_TABS.PREVIEW} />
								<TabPane tab={t('loc:Settings')} key={TEMPLATE_BUILDER_HEADER_TABS.SETTINGS} />
							</Tabs>
						</li>
					</ul>
					<div>
						<Spin className={'header-spinner'} indicator={antIcon} spinning={load} />
						<Dropdown className={'template-select'} menu={{ items: [] }}>
							<Button>{t('loc:Template')}</Button>
						</Dropdown>
					</div>
				</div>
				<div className={'right-box-form-builder-header'}>
					<Button className={'ant-btn-primary'} onClick={openAndInitFormModal} disabled={disable}>
						{t('loc:Create form')}
					</Button>
				</div>
			</div>
			<FormSettingsModal
				width={460}
				handleSubmit={createFormFromTemplate}
				isVisible={formModalShowed}
				title={t('loc:New form from template')}
				onCancel={() => {
					setFormModalShowed(false)
					dispatch(reset(FORM.GENERALE_TEMPLATE_ATTRIBUTES))
				}}
				cancelText={t('loc:Back')}
				onConfirmBtnTitle={t('loc:Create form')}
				disabled={processing}
			/>
		</>
	)
}

export default TemplateBuilderHeader
